import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiPlus, FiTrash2, FiSave, FiPackage, FiBox, FiChevronDown, FiChevronRight, FiCheckCircle, FiAlertCircle, FiArrowLeft, FiDownload, FiSearch, FiFilter, FiGrid, FiList } from 'react-icons/fi';
import { shipmentsAPI } from '../services/api';
import { toast } from 'react-toastify';

const PackageItems = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [boxes, setBoxes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [draggedUnit, setDraggedUnit] = useState(null);
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(false);
  const queryClient = useQueryClient();

  // Get shipment ID from URL parameters
  const shipmentId = new URLSearchParams(location.search).get('shipment');

  // Fetch specific shipment or all ready shipments
  const { data: masterShipments, isLoading: mastersLoading, refetch: refetchMasters } = useQuery(
    'master-shipments-ready',
    () => shipmentsAPI.getMasterShipments({ status: 'boxes_created' })
  );

  // Check if shipment is already packed
  const isAlreadyPacked = selectedShipment?.status === 'packed' || selectedShipment?.status === 'ready_to_ship';

  // Initialize boxes from shipment data
  useEffect(() => {
    if (masterShipments?.results?.length > 0) {
      let shipment;
      
      // Find specific shipment if ID provided, otherwise use first one
      if (shipmentId) {
        shipment = masterShipments.results.find(s => s.id === parseInt(shipmentId));
      } else {
        shipment = masterShipments.results[0];
      }
      
      if (shipment) {
        setSelectedShipment(shipment);
        
        // Always start with one empty box
        setBoxes([{
          id: `box-${Date.now()}`,
          box_name: 'Box 1',
          units: []
        }]);
      }
    }
  }, [masterShipments, shipmentId]);

  // Add new box
  const addBox = () => {
    const newBox = {
      id: `box-${Date.now()}`,
      box_name: `Box ${boxes.length + 1}`,
      units: []
    };
    setBoxes([...boxes, newBox]);
  };

  // Remove box (only if empty)
  const removeBox = (boxIndex) => {
    if (boxes[boxIndex].units.length === 0) {
      setBoxes(boxes.filter((_, i) => i !== boxIndex));
    } else {
      toast.error('Cannot remove box with units. Move units to other boxes first.');
    }
  };

  // Update box name
  const updateBoxName = (boxIndex, name) => {
    const updated = [...boxes];
    updated[boxIndex].box_name = name;
    setBoxes(updated);
  };

  // Save packaging
  const handleSave = async () => {
    if (totalUnits !== availableUnits.length) {
      toast.error('Please assign all units to boxes before saving');
      return;
    }

    if (!selectedShipment) {
      toast.error('No shipment selected');
      return;
    }

    setLoading(true);
    try {
      const packagingData = { 
        shipment_id: selectedShipment.id,
        boxes: boxes.map(box => ({
          box_name: box.box_name,
          units: box.units.map(unit => ({
            id: unit.id
          }))
        }))
      };
      
      console.log('DEBUG: Sending packaging data:', packagingData);
      
      // Call API to save the packaging structure
      await shipmentsAPI.savePackaging(packagingData);
      toast.success('Packaging saved successfully!');
      
      // Navigate back to shipments
      navigate('/shipments');
    } catch (error) {
      toast.error('Failed to save packaging');
      console.error('Error saving packaging:', error);
      console.error('Error response:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  // Calculate total units (excluding cancelled ones)
  const totalUnits = boxes.reduce((sum, box) => sum + box.units.length, 0);
  const availableUnits = selectedShipment?.units?.filter(unit => !unit.is_cancelled) || [];
  const assignedUnits = boxes.flatMap(box => box.units);
  const unassignedUnits = availableUnits.filter(unit => 
    !assignedUnits.some(assigned => assigned.id === unit.id)
  );

  // Filter units based on search and show unassigned only
  const filteredUnits = unassignedUnits.filter(unit => {
    const matchesSearch = !searchTerm || 
      unit.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      unit.item_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      unit.carrier_tracking_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  // Drag and drop handlers
  const handleDragStart = (e, unit) => {
    setDraggedUnit(unit);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, boxIndex) => {
    e.preventDefault();
    if (draggedUnit) {
      moveUnitToBox(draggedUnit.id, boxIndex);
      setDraggedUnit(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedUnit(null);
  };

  // Download labels for the shipment
  const handleDownloadLabels = () => {
    if (selectedShipment?.label_file) {
      const link = document.createElement('a');
      link.href = selectedShipment.label_file;
      link.download = `Master_Label_${selectedShipment.master_tracking_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Master label downloaded!');
    } else {
      toast.error('No master label available for this shipment');
    }
  };

  // Move unit to box
  const moveUnitToBox = (unitId, boxIndex) => {
    const unit = availableUnits.find(u => u.id === unitId);
    if (!unit) return;

    setBoxes(prevBoxes => 
      prevBoxes.map((box, index) => 
        index === boxIndex 
          ? { ...box, units: [...box.units, unit] }
          : box
      )
    );
  };

  // Remove unit from box
  const removeUnitFromBox = (unitId, boxIndex) => {
    setBoxes(prevBoxes => 
      prevBoxes.map((box, index) => 
        index === boxIndex 
          ? { ...box, units: box.units.filter(u => u.id !== unitId) }
          : box
      )
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/shipments')}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                <FiArrowLeft className="w-4 h-4 mr-2" />
                Back to Shipments
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">📦 Package Items</h1>
                <p className="text-sm text-gray-600">
                  {selectedShipment ? `Shipment: ${selectedShipment.master_tracking_number}` : 'Drag units into boxes to organize your shipment'}
                  {isAlreadyPacked && (
                    <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                      ✓ Already Packed
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                {totalUnits}/{availableUnits.length} units assigned
              </div>
              <button
                onClick={handleDownloadLabels}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                <FiDownload className="w-4 h-4 mr-2" />
                Download Labels
              </button>
              <button
                onClick={handleSave}
                disabled={loading || totalUnits !== availableUnits.length || isAlreadyPacked}
                className="flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FiSave className="w-4 h-4 mr-2" />
                {loading ? 'Saving...' : isAlreadyPacked ? 'Already Packed' : 'Save & Complete'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* Instructions */}
        {isAlreadyPacked ? (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-green-900 mb-2 flex items-center">
                  <FiCheckCircle className="w-5 h-5 mr-2" />
                  ✅ Shipment Already Packed
                </h3>
                <div className="text-green-700 space-y-1 text-sm">
                  <p>This shipment has already been packed and is ready for shipping.</p>
                  <p>You can view the packaging structure below, but no changes can be made.</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-green-600">{availableUnits.length}</div>
                <div className="text-sm text-green-500">Total Units</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-blue-900 mb-2 flex items-center">
                  <FiCheckCircle className="w-5 h-5 mr-2" />
                  🎯 Quick Start Guide
                </h3>
                <div className="text-blue-700 space-y-1 text-sm">
                  <p>1. <strong>Download labels</strong> and paste them on your items</p>
                  <p>2. <strong>Drag units</strong> from the list below into boxes</p>
                  <p>3. <strong>Create more boxes</strong> if needed</p>
                  <p>4. <strong>Save & Complete</strong> when all units are assigned</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-600">{availableUnits.length}</div>
                <div className="text-sm text-blue-500">Total Units</div>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <FiPackage className="w-5 h-5 mr-2" />
              Available Units ({unassignedUnits.length})
            </h3>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <FiGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <FiList className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search units by name, description, or tracking number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Units Horizontal Scrollable Layout */}
        {filteredUnits.length > 0 ? (
          <div className="mb-6">
            <div className="flex space-x-3 overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              {filteredUnits.map(unit => (
                <div
                  key={unit.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, unit)}
                  onDragEnd={handleDragEnd}
                  className="bg-white border border-gray-200 rounded-lg p-3 cursor-move hover:shadow-md transition-shadow hover:border-blue-300 group flex-shrink-0 w-64"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="font-medium text-gray-900 truncate text-sm">
                      {unit.customer_name || 'Unnamed'}
                    </div>
                    <div className="text-xs text-gray-400 group-hover:text-blue-500">
                      Drag →
                    </div>
                  </div>
                  <div className="text-xs text-gray-600 mb-2 truncate">
                    {unit.item_description}
                  </div>
                  <div className="text-xs text-gray-500 mb-1 truncate">
                    {unit.address_line1}, {unit.city}
                  </div>
                  <div className="text-xs text-blue-600 font-medium truncate">
                    {unit.carrier_tracking_number || 'No tracking'}
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center text-xs text-gray-500 mt-2">
              ← Scroll horizontally to see all units →
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <FiPackage className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No units found matching your search.</p>
          </div>
        )}

        {/* Boxes */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <FiBox className="w-5 h-5 mr-2" />
                📦 Boxes ({boxes.length} boxes, {totalUnits} units)
              </h3>
              <button
                onClick={addBox}
                className="flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 transition-colors"
              >
                <FiPlus className="w-4 h-4 mr-2" />
                Add Box
              </button>
            </div>
          </div>

          <div className="p-4">
            <div className="flex space-x-4 overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              {boxes.map((box, boxIndex) => (
                <div
                  key={box.id}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, boxIndex)}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-4 min-h-[200px] min-w-[300px] flex-shrink-0 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                >
                  <div className="flex items-center justify-between mb-3">
                    <input
                      type="text"
                      value={box.box_name}
                      onChange={(e) => updateBoxName(boxIndex, e.target.value)}
                      className="font-medium text-gray-900 bg-transparent border-none outline-none focus:bg-white focus:border focus:border-gray-300 focus:px-2 focus:py-1 focus:rounded"
                      placeholder={`Box ${boxIndex + 1}`}
                    />
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">
                        {box.units.length} units
                      </span>
                      <button
                        onClick={() => removeBox(boxIndex)}
                        className="text-red-400 hover:text-red-600 p-1"
                        title="Remove box"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2 min-h-[120px]">
                    {box.units.length > 0 ? (
                      box.units.map((unit, unitIndex) => (
                        <div
                          key={unit.id}
                          className="bg-white border border-gray-200 rounded p-2 text-sm"
                        >
                          <div className="font-medium text-gray-900 truncate">
                            {unit.customer_name || 'Unnamed'}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {unit.item_description}
                          </div>
                          <div className="text-xs text-blue-600">
                            {unit.carrier_tracking_number || 'No tracking'}
                          </div>
                          <button
                            onClick={() => removeUnitFromBox(unit.id, boxIndex)}
                            className="text-red-400 hover:text-red-600 text-xs mt-1"
                          >
                            Remove
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-gray-400 py-8">
                        <FiBox className="w-8 h-8 mx-auto mb-2" />
                        <p className="text-sm">Drop units here</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center text-xs text-gray-500 mt-2">
              ← Scroll horizontally to see all boxes →
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackageItems;
